import { useState, useEffect, useCallback } from 'react';
import { MapPin, Building2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { cn } from '@/lib/utils';
import { LoadingSpinner } from '@/components/ui';
import YandexMap from '@/components/ui/YandexMap';
import type { MapMarker } from '@/components/ui/YandexMap';
import { registrationApi } from '@/services/api/registrationApi';
import type { PublicBranch } from '@/services/api/registrationApi';
import { apiClient } from '@/services/api/client';
import { useAuthStore } from '@/stores/authStore';
import type { TokenResponse } from '@/types/api';
import { extractErrorMessage } from '@/utils/extractErrorMessage';

interface BranchSelectorProps {
  currentBranchId: number | null;
  onBranchChanged?: () => void;
}

const BranchSelector: React.FC<BranchSelectorProps> = ({ currentBranchId, onBranchChanged }) => {
  const [branches, setBranches] = useState<PublicBranch[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const fetchBranches = async () => {
      try {
        const data = await registrationApi.getPublicBranches();
        if (!cancelled) setBranches(data);
      } catch (err) {
        if (!cancelled) toast.error(extractErrorMessage(err, 'Не удалось загрузить пункты выдачи'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchBranches();
    return () => { cancelled = true; };
  }, []);

  const mapMarkers: MapMarker[] = branches
    .filter((b) => b.latitude != null && b.longitude != null)
    .map((b) => ({
      id: b.id,
      lat: b.latitude!,
      lng: b.longitude!,
      name: b.name,
    }));

  const handleSelect = useCallback(
    async (branchId: number) => {
      if (branchId === currentBranchId || saving) return;

      setSaving(true);
      try {
        const response = await apiClient.patch<TokenResponse>('/v1/my/branch', {
          branch_id: branchId,
        });

        const authStore = useAuthStore.getState();
        authStore.loginWithTokens(response.data);

        const { authApi } = await import('@/services/api/authApi');
        const user = await authApi.getMe();
        authStore.setUser(user);

        const branch = branches.find((b) => b.id === branchId);
        toast.success(branch ? `Пункт выдачи: ${branch.name}` : 'Пункт выдачи обновлён');

        onBranchChanged?.();
      } catch (err) {
        toast.error(extractErrorMessage(err, 'Не удалось сменить пункт выдачи'));
      } finally {
        setSaving(false);
      }
    },
    [currentBranchId, saving, branches, onBranchChanged],
  );

  if (loading) {
    return <LoadingSpinner className="py-8" />;
  }

  if (branches.length === 0) {
    return (
      <div className="text-center py-8 text-gray-400">
        <Building2 className="w-8 h-8 mx-auto mb-2" />
        <p className="text-sm">Нет доступных пунктов выдачи</p>
      </div>
    );
  }

  return (
    <div className={cn('relative', saving && 'pointer-events-none opacity-60')}>
      <YandexMap
        markers={mapMarkers}
        selectedMarkerId={currentBranchId}
        onMarkerClick={handleSelect}
        height="240px"
        className="mb-3"
      />

      <div className="space-y-1.5 max-h-48 overflow-y-auto pr-1">
        {branches.map((branch) => {
          const isSelected = branch.id === currentBranchId;
          return (
            <button
              key={branch.id}
              type="button"
              onClick={() => handleSelect(branch.id)}
              className={cn(
                'w-full text-left px-4 py-3 rounded-xl border transition-all duration-200 flex items-start gap-3',
                isSelected
                  ? 'border-primary-400 bg-primary-50 shadow-sm'
                  : 'border-gray-100 bg-white hover:border-gray-200 hover:bg-gray-50',
              )}
            >
              <MapPin
                className={cn(
                  'w-4 h-4 mt-0.5 flex-shrink-0',
                  isSelected ? 'text-primary-600' : 'text-gray-400',
                )}
              />
              <div className="min-w-0">
                <p
                  className={cn(
                    'text-sm font-medium truncate',
                    isSelected ? 'text-primary-700' : 'text-gray-900',
                  )}
                >
                  {branch.name}
                </p>
                {branch.address && (
                  <p className="text-xs text-gray-500 truncate mt-0.5">{branch.address}</p>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default BranchSelector;
